import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";

import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import Timeline from "components/common/Timeline";
import {
  convertDate,
  getValue,
  getDurationFromTwoDates,
  formatStatusLabel,
} from "components/Screens/CommonUtils/index";
import UnassignedEmployeeModal from "components/Screens/Workforce/UserDetails/NewPersonalInfo/EmploymentInfo/UnassignEmployee";
import AssignToCustomerModal from "components/Screens/Workforce/UserDetails/NewPersonalInfo/EmploymentInfo/AssignToCustomer";
import EditEmployeeStatus from "components/Screens/Workforce/UserDetails/NewPersonalInfo/EmploymentInfo/EditEmployeeStatus";
import WeeklyScheduleTooltip from "components/common/WeeklyScheduleTooltip";
import NoDataScreen from "components/Widgets/NoDataScreen";
import CustomTooltip from "components/common/Tooltip";
import CustomChip from "components/common/Chip";

import { getEmployeeStatus } from "services/BambooHrTabs/about";
import { getEmployeeSchedule } from "services/CheckInCheckOut/CheckInAndBreaks";

import { getChipStatusColor } from "util/utils";

import {
  PersonOffOutlined as PersonOffOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  AccessTime as AccessTimeIcon,
  EditOutlined as EditOutlinedIcon,
  WorkOutlineOutlined as WorkOutlineIcon,
} from "@mui/icons-material";

import "./EmploymentInfo.scss";

function UnAssignedEmployee({ onAssignToCustomerBtnClick }) {
  return (
    <div className="unassigned-employee-container">
      <WorkOutlineIcon />
      <div className="para-body-m-regular mb-05">This employee is not currently assigned to a customer.</div>
      <Button label="Assign to Customer" size="small" onClick={onAssignToCustomerBtnClick} variant="outlined" />
    </div>
  );
}

function CustomTooltipContent({ reason }) {
  const { reasonDate, reason: reasonMsg, comments } = reason || {};

  return (
    <div className="space-y-2">
      <div>
        <p className="para-body-xs-regular">Inactive since</p>
        <p className="para-body-xs-semibold">{convertDate(reasonDate)}</p>
      </div>
      <div>
        <p className="para-body-xs-regular">Reason for marking Inactive</p>
        <p className="para-body-xs-semibold">{getValue(reasonMsg)}</p>
      </div>
      <div>
        <p className="para-body-xs-regular">Additional comments</p>
        <p className="para-body-xs-semibold">{getValue(comments)}</p>
      </div>
    </div>
  );
}

function NoDataScreenContent({ reason }) {
  return (
    <div className="flex items-center">
      <p className="heading-h5-semibold pr-025">This employee is marked as</p>
      <CustomTooltip direction="right" content={<CustomTooltipContent reason={reason} />}>
        <div className="cursor-pointer">
          <CustomChip
            label="Inactive"
            startIcon={<InfoOutlinedIcon color="#000" />}
            color="default"
            variant="filled"
            size="small"
          />
        </div>
      </CustomTooltip>
    </div>
  );
}

function EmploymentInfo({ initialPersonalDataEmployment, employeeDetails, fetchEmployeeDetailsByIDV2 }) {
  const { capturingReasonsForInactiveEmployees, anniversaryBasedTimeOff, temporaryReplacement } = useFlags();

  const rowClassesForPersonalInfo = "flex justify-between w-100p mb-075";

  const [showAssignToCustomerModal, setShowAssignToCustomerModal] = useState(false);
  const [showAssignToCustomerModalForEdit, setShowAssignToCustomerModalForEdit] = useState(false);
  const [showUnassignmentModal, setShowUnassignmentModal] = useState(false);
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [schedule, setSchedule] = useState(null);

  const loadingGetEmployeeDetails = useSelector((state) => state.GetEmployeeDetails.loadingGET_EMPLOYEE_Details);
  const {
    companyName,
    addressLine1,
    city,
    state,
    country,
    zip,
    department,
    designation,
    employmentType,
    joiningDate,
    substituteOf,
    endDate,
    employmentHistoryId,
  } = employeeDetails?.customerInfo || {};

  const {
    startDate,
    status,
    salaryStatus,
    startDateHR,
    jobTitle: rootJobTitle,
    department: rootDepartment,
    employmentType: rootEmploymentType,
  } = employeeDetails || {};
  const { firstName, middleName, lastName, employeeStatus } =
    useSelector((s) => s.GetEmployeeDetails?.getEmployeeDetails)?.payload || {};

  const { employeeId } = initialPersonalDataEmployment || {};

  const [employmentStatusData, setEmploymentStatusData] = useState(null);

  const hiredDateValue = startDateHR || joiningDate;
  // Compare at day-granularity in the user's local timezone so a hired date of "today"
  // is not classified as future when the system clock is already past local midnight,
  // even if UTC midnight has not rolled over yet.
  const isHiredDateInFuture = (() => {
    if (!hiredDateValue) return false;
    const hired = new Date(hiredDateValue);
    hired.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return hired > today;
  })();
  const shouldShowStatusSection = !!hiredDateValue && !isHiredDateInFuture;

  // Prefer the timeline-derived currentStatus over the (possibly stale) Redux employeeStatus.
  // The backend self-heals the cache, but until the next page load the chip should reflect the
  // active timeline entry — same source the timeline list reads from.
  const displayedStatus = employmentStatusData?.currentStatus || employeeStatus;

  const fetchCurrentStatus = useCallback(async () => {
    if (!employeeId || salaryStatus !== "Complete" || !shouldShowStatusSection) return;
    try {
      const statusData = await getEmployeeStatus(employeeId);
      setEmploymentStatusData(statusData);
    } catch (error) {
      setEmploymentStatusData(null);
    }
  }, [employeeId, salaryStatus, shouldShowStatusSection]);

  const fetchSchedule = useCallback(async (employeeUserId) => {
    if (!employeeUserId) return;
    try {
      const response = await getEmployeeSchedule(employeeUserId);
      if (response?.schedule) {
        setSchedule(response.schedule);
      }
    } catch (error) {
      setSchedule(null);
    }
  }, []);

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const getCurrentDaySchedule = () => {
    if (!schedule || !Array.isArray(schedule)) return null;
    return schedule.find((day) => day.dayOfWeek === new Date().getDay());
  };

  const getCurrentDayShiftDisplay = () => {
    const daySchedule = getCurrentDaySchedule();
    const isOff = !daySchedule || !daySchedule.startTime || !daySchedule.endTime;
    return isOff ? "Off" : `${formatTime(daySchedule.startTime)} - ${formatTime(daySchedule.endTime)}`;
  };

  useEffect(() => {
    if (!employeeId) return;
    fetchCurrentStatus();
  }, [employeeId, status, salaryStatus, startDate, startDateHR]);

  useEffect(() => {
    if (!employeeId) return;
    fetchSchedule(employeeId);
  }, [employeeId, fetchSchedule]);

  // Normalize to local-day boundaries so "today" is compared at day-granularity in the
  // user's timezone — a status starting "today" should read as Active, not Starts.
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const employmentTimelineData =
    employmentStatusData?.timeline
      ?.map((employmentStatusObj, index) => {
        const { startDate: statusStartDate, endDate: statusEndDate, status: statusValue } = employmentStatusObj;

        const start = statusStartDate ? new Date(statusStartDate) : null;
        if (start) start.setHours(0, 0, 0, 0);
        const end = statusEndDate ? new Date(statusEndDate) : null;
        if (end) end.setHours(0, 0, 0, 0);

        let subtext = formatStatusLabel(statusValue);
        let date = `${convertDate(statusStartDate)} - ${convertDate(statusEndDate) || "Present"}`;
        let duration = getDurationFromTwoDates(statusStartDate, statusEndDate);

        const isCurrent = start && today >= start && (!end || today <= end);

        if (start && start > today) {
          subtext = `Starts ${convertDate(statusStartDate)}`;
          date = null;
          duration = null;
        } else if (statusValue === "Probation" || statusValue === "Notice_Period") {
          if (start && end && today >= start && today <= end) {
            subtext = `Active till ${convertDate(statusEndDate)}`;
          } else if (end && today > end) {
            subtext = "Completed";
          }
        } else if (statusValue === "Permanent") {
          if (end) {
            subtext = today > end ? "Completed" : `Active till ${convertDate(statusEndDate)}`;
          } else if (isCurrent) {
            subtext = "Active";
          }
        }

        if (isCurrent) {
          date = `${convertDate(statusStartDate)} - Present`;
          duration = getDurationFromTwoDates(statusStartDate, today);
        }

        return {
          id: index + 1,
          title: formatStatusLabel(statusValue),
          subtext: formatStatusLabel(subtext),
          date,
          duration,
        };
      })
      .reverse() || [];

  const renderContent = () => {
    if (status === "unassigned") {
      return <UnAssignedEmployee onAssignToCustomerBtnClick={() => setShowAssignToCustomerModal(true)} />;
    }

    if (status === "inactive" && capturingReasonsForInactiveEmployees) {
      return (
        <div className="no-screen-wrapper">
          <NoDataScreen
            icon={
              <div className="no-screen-inactive-icon">
                <PersonOffOutlinedIcon sx={{ fontSize: 20, color: "#AFAFAF" }} />
              </div>
            }
            content={<NoDataScreenContent reason={employeeDetails?.reason} />}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-15">
        <div>
          <div className={rowClassesForPersonalInfo}>
            <div className="heading-h4-medium">Employment details</div>
          </div>
          <div className={rowClassesForPersonalInfo}>
            <div className="heading-h5-regular">Company Name</div>
            <div className="para-body-m-semibold">{getValue(companyName)}</div>
          </div>

          <div className={rowClassesForPersonalInfo}>
            <div className="heading-h5-regular">Department</div>
            <div className="para-body-m-semibold">{getValue(department)}</div>
          </div>

          <div className={rowClassesForPersonalInfo}>
            <div className="heading-h5-regular">Job Title</div>
            <div className="para-body-m-semibold">{getValue(designation)}</div>
          </div>

          <div className={rowClassesForPersonalInfo}>
            <div className="heading-h5-regular">Hired Date</div>
            {isHiredDateInFuture ? (
              <CustomChip
                variant="filled"
                size="small"
                color="warning"
                label={`Scheduled for ${convertDate(hiredDateValue)}`}
                startIcon={<AccessTimeIcon />}
              />
            ) : (
              <div className="para-body-m-semibold">{convertDate(hiredDateValue)}</div>
            )}
          </div>

          <div className={rowClassesForPersonalInfo}>
            <div className="heading-h5-regular">Company Address</div>
            <div className="para-body-m-semibold">
              {getValue(`${addressLine1 || ""} ${city || ""} ${state || ""} ${country || ""} ${zip || ""} `)}
            </div>
          </div>

          <div className={rowClassesForPersonalInfo}>
            <p className="heading-h5-regular">Shift timings</p>
            <div className="flex items-center gap-025">
              <div className="para-body-m-semibold">{getCurrentDayShiftDisplay()}</div>
              <WeeklyScheduleTooltip isAdmin employeeId={employeeId} schedule={schedule} />
            </div>
          </div>
        </div>

        {anniversaryBasedTimeOff && displayedStatus && salaryStatus === "Complete" && shouldShowStatusSection && (
          <div className="status-details-section">
            <div className={rowClassesForPersonalInfo}>
              <div className="heading-h4-medium">Status details</div>
              {displayedStatus && displayedStatus !== "Substitute" && (
                <CustomChip
                  label={formatStatusLabel(displayedStatus)}
                  color={getChipStatusColor(displayedStatus)}
                  size="small"
                  variant="filled"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => setShowEditStatusModal(true)}
                />
              )}
              {temporaryReplacement && displayedStatus && displayedStatus === "Substitute" && (
                <CustomChip
                  label={formatStatusLabel(displayedStatus)}
                  color={getChipStatusColor(displayedStatus)}
                  size="small"
                  variant="filled"
                />
              )}
            </div>
            {displayedStatus && displayedStatus !== "Substitute" && (
              <Timeline timelineData={employmentTimelineData} showDivider={false} variant="sm" />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {loadingGetEmployeeDetails && <PageLoader />}

      <AssignToCustomerModal
        employeeId={employeeId}
        open={showAssignToCustomerModal || showAssignToCustomerModalForEdit}
        showAssignToCustomerModalForEdit={showAssignToCustomerModalForEdit}
        employmentStatus={status}
        initialEmploymentDetails={
          showAssignToCustomerModalForEdit
            ? {
                employmentType: employmentType || rootEmploymentType,
                dateOfJoining: startDate,
                jobTitle: designation || rootJobTitle,
                department: department || rootDepartment,
                company: companyName,
                substituteOf,
                endDate,
                assignmentId: employmentHistoryId,
                employeeHrStartDate: startDateHR || null,
              }
            : { jobTitle: rootJobTitle || "" }
        }
        fetchEmployeeDetailsByIDV2={fetchEmployeeDetailsByIDV2}
        closeModal={() => {
          setShowAssignToCustomerModal(false);
          setShowAssignToCustomerModalForEdit(false);
        }}
      />

      <UnassignedEmployeeModal
        fetchEmployeeDetailsByIDV2={fetchEmployeeDetailsByIDV2}
        employeeId={employeeId}
        companyName={companyName}
        name={`${firstName} ${middleName || ""} ${lastName}`}
        open={showUnassignmentModal}
        closeModal={() => setShowUnassignmentModal(false)}
        scheduledAction={employeeDetails?.scheduledAction}
      />

      <EditEmployeeStatus
        open={showEditStatusModal}
        closeModal={() => setShowEditStatusModal(false)}
        employmentStatusData={employmentStatusData}
        fetchCurrentStatus={fetchCurrentStatus}
        employeeId={employeeId}
      />

      <div className="flex justify-between w-100p mb-15">
        <div className="flex items-center gap-05">
          <WorkOutlineIcon className="color-icon-primary admin-overview-heading-icon" alt="work" />
          <div className="heading-h4-semibold">Employment Info</div>
        </div>

        {status !== "unassigned" && status !== "inactive" && (
          <div className="flex gap-1">
            <Button
              label="Un-assign From Customer"
              size="small"
              onClick={() => setShowUnassignmentModal(true)}
              variant="outlined"
            />
            <Button
              label="Edit"
              size="small"
              onClick={() => setShowAssignToCustomerModalForEdit(true)}
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
            />
          </div>
        )}
      </div>
      {renderContent()}
    </>
  );
}

export default EmploymentInfo;
